import React, { useEffect, useMemo, useRef, useState } from "react";
import "./SearchableSelect.css";

const SearchableSelect = ({
  options = [],
  valueId = "",
  valueLabel = "",
  onChange,
  placeholder = "Rechercher un fournisseur…",
  inputClassName = "form-input",
  disabled = false,
}) => {
  const [query, setQuery] = useState(valueLabel || "");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      setQuery(valueLabel || "");
    }
  }, [valueLabel, valueId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = Array.isArray(options) ? options : [];
    if (!q) return list.slice(0, 50);
    return list
      .filter((opt) => String(opt.name || "").toLowerCase().includes(q))
      .slice(0, 50);
  }, [options, query]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        if (valueId) setQuery(valueLabel || "");
        else {
          const exact = (options || []).find(
            (opt) => String(opt.name || "").toLowerCase() === query.trim().toLowerCase()
          );
          if (exact) {
            onChange?.({ id: exact.id, name: exact.name });
            setQuery(exact.name);
          } else {
            setQuery("");
            onChange?.({ id: "", name: "" });
          }
        }
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [options, query, valueId, valueLabel, onChange]);

  const selectOption = (opt) => {
    onChange?.({ id: opt.id, name: opt.name });
    setQuery(opt.name);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[highlight]) selectOption(filtered[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery(valueLabel || "");
    }
  };

  return (
    <div className="searchable-select" ref={wrapRef}>
      <input
        ref={inputRef}
        type="text"
        className={`${inputClassName} searchable-select-input`}
        value={query}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="off"
        onFocus={() => {
          setOpen(true);
          setHighlight(0);
        }}
        onChange={(e) => {
          const next = e.target.value;
          setQuery(next);
          setOpen(true);
          setHighlight(0);
          if (valueId && next !== valueLabel) onChange?.({ id: "", name: "" });
        }}
        onKeyDown={handleKeyDown}
      />
      {valueId && (
        <button
          type="button"
          className="searchable-select-clear"
          onClick={() => {
            onChange?.({ id: "", name: "" });
            setQuery("");
            inputRef.current?.focus();
            setOpen(true);
          }}
          aria-label="Effacer"
        >
          ×
        </button>
      )}
      {open && (
        <div className="searchable-select-menu">
          {filtered.length === 0 ? (
            <div className="searchable-select-empty">Aucun fournisseur trouvé</div>
          ) : (
            filtered.map((opt, idx) => (
              <button
                type="button"
                key={opt.id}
                className={`searchable-select-option${idx === highlight ? " is-active" : ""}${
                  String(opt.id) === String(valueId) ? " is-selected" : ""
                }`}
                onMouseEnter={() => setHighlight(idx)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectOption(opt);
                }}
              >
                {opt.name}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
